import React from "react";
import { DndContext, DragEndEvent, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { GripVertical, Pin } from "lucide-react";
import { RowKind, RowIcon, OwnerCell, formatRelative } from "./category-rows";
import { User } from "./user-display";

export interface Row {
    kind: RowKind;
    id: string;
    title: string;
    modifiedAt: string;
    ownerId: string | null;
    onClick: () => void;
    isPinned?: boolean;
    subtext?: string;
    description?: string | null;
    rowKebab?: React.ReactNode;
    iconName?: string | null;
    iconColor?: string | null;
    filename?: string | null;
}

// The server order is already SortOrder, so a stable partition is all it takes.
export function withPinnedFirst(rows: Row[]): Row[] {
    return [...rows.filter(r => r.isPinned), ...rows.filter(r => !r.isPinned)];
}

interface BodyProps {
    rows: Row[];
    showDescription: boolean;
    users: Map<string, User>;
    canReorder: boolean;
    onReorder: (kind: RowKind, orderedIds: string[]) => void;
}

export function CatTableBody({ rows, showDescription, users, canReorder, onReorder }: BodyProps) {
    // A few pixels of travel before a drag starts, so a click still opens the row.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const pinned = rows.filter(r => r.isPinned);
    const rest = rows.filter(r => !r.isPinned);

    // One sortable group per kind: each kind's SortOrder lives in its own table, so
    // an order spanning two of them can't be expressed.
    const groups: { kind: RowKind; items: Row[] }[] = [];
    pinned.forEach(row => {
        const group = groups.find(g => g.kind === row.kind);
        if (group) group.items.push(row);
        else groups.push({ kind: row.kind, items: [row] });
    });

    function handleDragEnd(kind: RowKind, items: Row[], e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const from = items.findIndex(r => r.id === active.id);
        const to = items.findIndex(r => r.id === over.id);
        if (from < 0 || to < 0) return;
        onReorder(kind, arrayMove(items, from, to).map(r => r.id));
    }

    return (
        <>
            {groups.map(group => (
                <DndContext
                    key={`sortable-${group.kind}`}
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={e => handleDragEnd(group.kind, group.items, e)}
                >
                    <SortableContext items={group.items.map(r => r.id)} strategy={verticalListSortingStrategy}>
                        {group.items.map(row => (
                            <SortableRow
                                key={`${row.kind}-${row.id}`}
                                row={row}
                                showDescription={showDescription}
                                users={users}
                                draggable={canReorder && group.items.length > 1}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ))}
            {rest.map(row => (
                <div key={`${row.kind}-${row.id}`} className="m-cat-table-row" onClick={row.onClick}>
                    <RowCells row={row} showDescription={showDescription} users={users} />
                </div>
            ))}
        </>
    );
}

function SortableRow({ row, showDescription, users, draggable }: {
    row: Row;
    showDescription: boolean;
    users: Map<string, User>;
    draggable: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row.id,
        disabled: !draggable,
    });

    return (
        <div
            ref={setNodeRef}
            className={`m-cat-table-row m-cat-row-pinned${isDragging ? " m-cat-row-dragging" : ""}`}
            style={{
                transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
                transition,
            }}
            onClick={row.onClick}
        >
            {draggable && (
                <button
                    className="m-cat-row-grip"
                    title="Drag to reorder pinned items"
                    onClick={e => e.stopPropagation()}
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical size={14} />
                </button>
            )}
            <RowCells row={row} showDescription={showDescription} users={users} />
        </div>
    );
}

function RowCells({ row, showDescription, users }: {
    row: Row;
    showDescription: boolean;
    users: Map<string, User>;
}) {
    return (
        <>
            <div className="m-cat-table-name">
                <RowIcon
                    kind={row.kind}
                    filename={row.kind === "document" ? row.title : undefined}
                    iconName={row.iconName}
                    iconColor={row.iconColor}
                />
                <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.title}
                        {row.isPinned && <Pin size={13} className="m-cat-row-pin" fill="currentColor" />}
                    </span>
                    {row.subtext && <span className="m-cat-row-subtext">{row.subtext}</span>}
                </span>
            </div>
            {showDescription && (
                <div className="m-cat-table-description">
                    {row.description || <span className="m-cat-row-muted">—</span>}
                </div>
            )}
            <div className="m-cat-table-modified">{formatRelative(row.modifiedAt)}</div>
            <div className="m-cat-table-owner">
                <OwnerCell userId={row.ownerId} users={users} />
                {row.rowKebab}
            </div>
        </>
    );
}
